import type {
  DeleteBookingResponse,
  DeleteProfessionalBookingParams,
  GetAvailableSlotsInput,
  GetAvailableSlotsOutput,
  ProfessionalBooking
} from '@api/models';
import {
  BookingPaymentStatus,
  BookingResponse,
  EnhancedBookingResponse,
  GetProfessionalBookingsLocale,
  GetProfessionalBookingsSortType,
  GetUserBookingsLocale,
  GetUserBookingsParams,
  GetUserBookingsPaymentStatus,
  GetUserBookingsSortType,
  ProfessionalBookingBaseResponse,
  ProfessionalBookingBookingType,
  ProfessionalBookingResponse
} from '@api/models';

import { getBookingEndDateTime } from '@website/app/[locale]/bookings/_utils/bookingDateUtils';
import { ApiClient } from '@website/lib/api';
import { BrowserApiClient } from '@website/lib/api/browser';
import { browserFetch } from '@website/lib/api/browserFetch';
import { getAssetUrl } from '@website/utils/assets';
import { formatDateAsLocalString } from '@website/utils/date';
import {
  convertUtcTimeSlotToLocal,
  formatInstantTime,
  formatTimeWithLuxon,
  instantLocalDay,
  slotLocalDay
} from '@website/utils/timezone';

/**
 * Extract user-friendly error message from API error.
 * API errors have format: "Call to /endpoint\nHTTP status\n{...}\n"Actual message""
 * This extracts just the meaningful message for display to users.
 */
function extractErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof Error) {
    // Try to extract the actual message (last line in quotes)
    const match = error.message.match(/"([^"]+)"$/);
    return match ? match[1] : error.message;
  }
  return fallbackMessage;
}

// Re-export for convenience - uses generated enum from API
export type PaymentStatus = `${BookingPaymentStatus}`;

export interface BookingData {
  id: string;
  activityId: string;
  activityTitle: string;
  /** The pro's conditions/disclaimer for the booked activity (localized). */
  activityDisclaimer?: string;
  professionalId: string;
  professionalName: string;
  professionalEmail?: string;
  professionalPhone?: string;
  date: string;
  time: string;
  /** Frozen absolute instants (epoch ms) - for zone-robust calendar export. */
  eventStartAt?: number;
  eventEndAt?: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  paymentStatus?: PaymentStatus;
  location?: string;
  numberOfPeople: number;
  totalPrice: number;
  notes?: string;
  bookingType: string;
  slotId?: string;
  /** Parent rule id when this booking is attached to a materialized child slot. */
  parentSlotId?: string;
  activityImageUrl?: string;
  activityDescription?: string;
  mediaUrls?: string[];
  isAtHome?: boolean;
  /** Resolved delivery kind of the booked location (venue | at_home | online). */
  locationKind?: 'venue' | 'at_home' | 'online';
  /** Online meeting (read-only): how the room is sourced + BYO url + delivery time. */
  meetingProvider?: 'toptoptime' | 'byo';
  meetingUrl?: string;
  meetingLinkSentAt?: number;
  activityLocationAddress?: string;
  activityLocationLatitude?: number;
  activityLocationLongitude?: number;
  addressLatitude?: number;
  addressLongitude?: number;
  clientInfo?: {
    clientId?: string;
    name: string;
    email?: string;
    phone?: string;
    isRegisteredUser: boolean;
  };
  // Dispute information
  disputeStatus?: 'pending' | 'accepted' | 'rejected';
  disputeReason?: string;
  disputeOpenedAt?: number;
  // HIGH-6: Dispute eligibility info from backend (single source of truth)
  disputeInfo?: {
    canDispute: boolean;
    disputeWindowEndsAt?: number;
    reason?: string;
  };
  // Cancellation information
  cancelledAt?: number;
  cancelledBy?: 'user' | 'professional' | 'admin' | 'system';
  cancellationReason?: string;
  platformFee?: number;
}

export const formatTimeSlotWithTimezone = (startTime: number, endTime: number, bookingDate: string): string => {
  try {
    const utcDate = new Date(bookingDate + 'T00:00:00.000Z');
    const localConversion = convertUtcTimeSlotToLocal(startTime, endTime, utcDate);
    const localStartTime = formatTimeWithLuxon(localConversion.startTime);
    const localEndTime = formatTimeWithLuxon(localConversion.endTime);
    return `${localStartTime} - ${localEndTime}`;
  } catch {
    const hours1 = Math.floor(startTime / 60);
    const mins1 = startTime % 60;
    const hours2 = Math.floor(endTime / 60);
    const mins2 = endTime % 60;
    const start = `${hours1.toString().padStart(2, '0')}:${mins1.toString().padStart(2, '0')}`;
    const end = `${hours2.toString().padStart(2, '0')}:${mins2.toString().padStart(2, '0')}`;
    return `${start} - ${end}`;
  }
};

export const transformBookingData = (
  booking: BookingResponse | EnhancedBookingResponse | ProfessionalBookingResponse
): BookingData => {
  // Prefer the server-resolved absolute instants (`eventStartAt`/`eventEndAt`),
  // rendered in the viewer's browser zone. Fall back to the venue-anchored
  // minutes path for any payload that predates the instant fields.
  const eventStartAt = (booking as any).eventStartAt as number | undefined;
  const eventEndAt = (booking as any).eventEndAt as number | undefined;

  const time =
    eventStartAt !== undefined && eventEndAt !== undefined
      ? `${formatInstantTime(eventStartAt)} - ${formatInstantTime(eventEndAt)}`
      : booking.startTime !== undefined && booking.endTime !== undefined
        ? formatTimeSlotWithTimezone(booking.startTime, booking.endTime, booking.bookingDate)
        : 'Time TBD';

  // Derive the displayed calendar day from the same instant as the time, so a
  // boundary-crossing booking doesn't show a date one day off from its time.
  // (Display/sort only - the raw UTC bookingDate stays the server identity.)
  const date =
    eventStartAt !== undefined
      ? formatDateAsLocalString(instantLocalDay(eventStartAt))
      : booking.startTime !== undefined && booking.bookingDate
        ? formatDateAsLocalString(slotLocalDay(booking.bookingDate, booking.startTime))
        : booking.bookingDate || 'Date TBD';

  let location = '';
  {
    const parts = [
      booking.addressLine1,
      booking.addressLine2,
      booking.addressCity,
      booking.addressState,
      booking.addressZipCode,
      booking.addressCountry
    ].filter(Boolean);
    location = parts.join(', ');
  }

  const isEnhancedBooking = (
    b: BookingResponse | EnhancedBookingResponse | ProfessionalBookingResponse
  ): b is EnhancedBookingResponse => {
    return 'activityTitle' in b && 'professionalName' in b;
  };

  const isProfessionalBooking = (
    b: BookingResponse | EnhancedBookingResponse | ProfessionalBookingResponse
  ): b is ProfessionalBookingResponse => {
    return 'clientInfo' in b;
  };

  const enhanced = isEnhancedBooking(booking) ? booking : null;
  const professional = isProfessionalBooking(booking) ? booking : null;

  return {
    id: booking.id,
    activityId: booking.activityId,
    eventStartAt,
    eventEndAt,
    activityTitle:
      enhanced?.activityTitle || professional?.activityTitle || `Activity ${booking.activityId.slice(0, 8)}`,
    activityDisclaimer: enhanced?.activityDisclaimer || professional?.activityDisclaimer || undefined,
    professionalId: booking.professionalId,
    professionalName: enhanced?.professionalName || `Professional ${booking.professionalId.slice(0, 8)}`,
    professionalEmail: enhanced?.professionalEmail || '',
    professionalPhone: enhanced?.professionalPhone || '',
    date,
    time,
    status: (booking as any).cancelledAt ? 'cancelled' : 'confirmed',
    paymentStatus: (booking as any).paymentStatus as PaymentStatus | undefined,
    location: location || undefined,
    numberOfPeople: ('numberOfPeople' in booking ? booking.numberOfPeople : null) || professional?.numberOfPeople || 1,
    totalPrice: booking.totalPrice || 0,
    notes: booking.notes,
    bookingType: booking.bookingType || 'SINGLE',
    slotId: booking.slotId,
    parentSlotId: (booking as any).parentSlotId ?? undefined,
    activityImageUrl: enhanced?.activityImageKey
      ? getAssetUrl(enhanced.activityImageKey)
      : professional?.activityImageKey
        ? getAssetUrl(professional.activityImageKey)
        : getAssetUrl('activity_placeholder.webp'),
    activityDescription: undefined,
    mediaUrls: undefined,
    isAtHome: enhanced?.isAtHome || professional?.isAtHome,
    locationKind: (booking as any).locationKind,
    meetingProvider: (booking as any).meetingProvider,
    meetingUrl: (booking as any).meetingUrl,
    meetingLinkSentAt: (booking as any).meetingLinkSentAt,
    activityLocationAddress:
      (enhanced as any)?.activityLocationAddress || (professional as any)?.activityLocationAddress,
    activityLocationLatitude:
      (enhanced as any)?.activityLocationLatitude ?? (professional as any)?.activityLocationLatitude,
    activityLocationLongitude:
      (enhanced as any)?.activityLocationLongitude ?? (professional as any)?.activityLocationLongitude,
    addressLatitude: (booking as any).addressLatitude,
    addressLongitude: (booking as any).addressLongitude,
    clientInfo: professional?.clientInfo
      ? {
          clientId: professional.clientInfo.clientId ?? `anon_${booking.id?.slice(0, 16) ?? 'unknown'}`,
          name: professional.clientInfo.name,
          email: professional.clientInfo.email,
          phone: professional.clientInfo.phone,
          isRegisteredUser: professional.clientInfo.isRegisteredUser
        }
      : undefined,
    // Dispute information
    disputeStatus: (booking as any).disputeStatus,
    disputeReason: (booking as any).disputeReason,
    disputeOpenedAt: (booking as any).disputeOpenedAt,
    // Cancellation information
    cancelledAt: (booking as any).cancelledAt,
    cancelledBy: (booking as any).cancelledBy,
    cancellationReason: (booking as any).cancellationReason,
    platformFee: (booking as any).platformFee
  };
};

export interface BookingFilters {
  startDate?: string;
  endDate?: string;
  activityId?: string | string[];
  cursor?: string;
  limit?: number;
  sortType?: GetUserBookingsSortType | GetProfessionalBookingsSortType;
  locale?: string;
  /** Filter by client identifier (userId or ext_* hash for external clients) */
  menteeClientId?: string;
}

export interface BookingQueryResult {
  upcoming: BookingData[];
  past: BookingData[];
  cancelled: BookingData[];
  pendingPayment: BookingData[];
  expired: BookingData[];
  total: number;
  nextCursor?: string;
}

export const getUnifiedBookings = async (
  userType: 'user' | 'professional',
  filters: BookingFilters = {}
): Promise<BookingQueryResult> => {
  try {
    // Fetch ALL pages of bookings sorted by date ASC, then filter client-side
    const allBookings: (BookingResponse | EnhancedBookingResponse | ProfessionalBookingResponse)[] = [];
    let cursor: string | undefined = undefined;

    do {
      const response: {
        bookings: (BookingResponse | EnhancedBookingResponse | ProfessionalBookingResponse)[];
        nextCursor?: string;
      } =
        userType === 'professional'
          ? await ApiClient.getProfessionalBookings({
              sortType: GetProfessionalBookingsSortType.DESC,
              ...(filters.activityId && { activityId: filters.activityId }),
              ...(filters.locale && { locale: filters.locale as GetProfessionalBookingsLocale }),
              ...(filters.startDate && { startDate: filters.startDate }),
              ...(filters.endDate && { endDate: filters.endDate }),
              ...(cursor && { cursor })
            })
          : await ApiClient.getUserBookings({
              sortType: GetUserBookingsSortType.DESC,
              ...(filters.activityId && { activityId: filters.activityId }),
              ...(filters.locale && { locale: filters.locale as GetUserBookingsLocale }),
              ...(filters.startDate && { startDate: filters.startDate }),
              ...(filters.endDate && { endDate: filters.endDate }),
              ...(cursor && { cursor })
            });

      allBookings.push(...response.bookings);
      cursor = response.nextCursor;
    } while (cursor);

    const transformedBookings = allBookings.map(transformBookingData);

    // Separate cancelled bookings from active ones
    const cancelled = transformedBookings.filter((booking) => booking.status === 'cancelled');
    const activeBookings = transformedBookings.filter((booking) => booking.status !== 'cancelled');

    // Absolute end instant of a booking. Prefer the server-frozen `eventEndAt`
    // (zone-robust epoch ms); fall back to re-parsing the display date/time for
    // legacy payloads that predate the instant fields. A booking counts as past
    // only once this end instant has passed, so it stays in "upcoming" for its
    // whole duration (until it is actually completed), not from its start time.
    const endInstant = (b: BookingData) => b.eventEndAt ?? getBookingEndDateTime(b.date, b.time).getTime();

    // Separate pending payment and expired bookings into their own buckets
    const pendingPayment = activeBookings
      .filter((b) => b.paymentStatus === 'PENDING_PAYMENT')
      .sort((a, b) => endInstant(a) - endInstant(b));
    const expired = activeBookings.filter((b) => b.paymentStatus === 'EXPIRED');
    const confirmedBookings = activeBookings.filter(
      (b) => b.paymentStatus !== 'PENDING_PAYMENT' && b.paymentStatus !== 'EXPIRED'
    );

    // Filter by end instant (not just date) to properly classify bookings.
    const now = Date.now();
    const upcoming = confirmedBookings
      .filter((booking) => endInstant(booking) >= now)
      // Soonest booking first
      .sort((a, b) => endInstant(a) - endInstant(b));
    const past = confirmedBookings
      .filter((booking) => endInstant(booking) < now)
      // Most recently past first
      .sort((a, b) => endInstant(b) - endInstant(a));

    return {
      upcoming,
      past,
      cancelled,
      pendingPayment,
      expired,
      total: transformedBookings.length,
      nextCursor: undefined // No pagination
    };
  } catch {
    return { upcoming: [], past: [], cancelled: [], pendingPayment: [], expired: [], total: 0, nextCursor: undefined };
  }
};

export const getBookings = async (
  params?: GetUserBookingsParams,
  _type: 'upcoming' | 'past' | 'all' = 'all'
): Promise<BookingQueryResult> => {
  const filters: BookingFilters = {
    limit: params?.limit,
    cursor: params?.cursor,
    activityId: params?.activityId,
    startDate: params?.startDate,
    endDate: params?.endDate,
    sortType: params?.sortType
  };

  return getUnifiedBookings('user', filters);
};

export const getBookingDetails = async (bookingId: string): Promise<BookingResponse> => {
  const response = await ApiClient.getBookingById(bookingId);
  return response;
};

/**
 * Get booking by ID for polling booking status after cancellation
 * Returns raw API response with paymentStatus
 */
export const getBookingById = async (
  bookingId: string,
  includeCancelled?: boolean
): Promise<{ paymentStatus?: string }> => {
  const response = await ApiClient.getBookingById(
    bookingId,
    includeCancelled ? ({ includeCancelled: true } as any) : undefined
  );
  return { paymentStatus: response.paymentStatus };
};

/**
 * Parameters for finding a booking for a specific slot
 */
export interface FindBookingForSlotParams {
  activityId: string;
  slotId: string;
  bookingDate: string;
  startTime: number;
  endTime: number;
}

/**
 * Find any active booking that matches the given slot criteria.
 * Used to recover a booking when user reloads the payment page.
 *
 * B0.2: By default, excludes PENDING_PAYMENT bookings from list views. For payment
 * recovery, set includePendingPayment=true to find in-progress payment bookings.
 *
 * @param params.includePendingPayment If true, explicitly requests PENDING_PAYMENT bookings
 * @returns The matching booking (any status), or null if not found
 */
export const findBookingForSlot = async (
  params: FindBookingForSlotParams & { includePendingPayment?: boolean }
): Promise<EnhancedBookingResponse | null> => {
  try {
    const response = await BrowserApiClient.getUserBookings({
      activityId: params.activityId,
      slotId: params.slotId,
      bookingDate: params.bookingDate,
      startTime: params.startTime,
      endTime: params.endTime,
      limit: 1,
      // B0.2: Explicitly request PENDING_PAYMENT when recovering payment flow
      ...(params.includePendingPayment && { paymentStatus: GetUserBookingsPaymentStatus.PENDING_PAYMENT })
    });

    // Return the first (and should be only) matching booking
    return response.bookings?.[0] || null;
  } catch (error) {
    console.error('Error finding booking for slot:', error);
    return null;
  }
};

// Keep old name as alias for backwards compatibility
export const findPendingBookingForPayment = findBookingForSlot;
export type FindPendingBookingParams = FindBookingForSlotParams;

export async function getAvailableSlots(input: GetAvailableSlotsInput): Promise<GetAvailableSlotsOutput> {
  return ApiClient.getAvailableSlots(input);
}

export interface CreateProfessionalBookingData {
  activityId: string;
  slotId: string;
  bookingDate: string;
  bookingType: 'SINGLE' | 'GROUP' | 'RANGE';
  /** Party size for GROUP bookings (defaults to 1). */
  numberOfPeople?: number;
  notes?: string;
  client: {
    name: string;
    /** Optional - omitted from the request when empty. */
    email?: string;
    /** Optional - omitted from the request when empty. */
    contact?: string;
  };
  timeSlot: {
    startTime: number;
    endTime: number;
  };
  /** Resolved activity_location id (opaque uuid) for the booked delivery mode. */
  locationId?: string;
  address?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  /**
   * Geocoded coordinates of the at-home `address`. The backend runs
   * `validateAddressInRadius` and rejects an at-home booking without these when
   * the location has a service radius, so the take flow geocodes the typed
   * address and forwards lat/long here.
   */
  addressLatitude?: number;
  addressLongitude?: number;
}

export interface CreateProfessionalBookingResult {
  success: boolean;
  booking?: ProfessionalBookingBaseResponse;
  error?: string;
  message?: string;
}

export interface DeleteProfessionalBookingResult {
  success: boolean;
  data?: DeleteBookingResponse;
  error?: string;
  message?: string;
}

export async function createProfessionalBooking(
  data: CreateProfessionalBookingData
): Promise<CreateProfessionalBookingResult> {
  try {
    const bookingType: ProfessionalBookingBookingType =
      data.bookingType === 'GROUP'
        ? ProfessionalBookingBookingType.GROUP
        : data.bookingType === 'RANGE'
          ? ProfessionalBookingBookingType.RANGE
          : ProfessionalBookingBookingType.SINGLE;

    const request: ProfessionalBooking = {
      activityId: data.activityId,
      slotId: data.slotId,
      bookingDate: data.bookingDate,
      bookingType: bookingType,
      numberOfPeople: data.bookingType === 'GROUP' ? (data.numberOfPeople ?? 1) : 1,
      notes: data.notes?.trim() || undefined,
      clientName: data.client.name.trim(),
      clientEmail: data.client.email?.trim() ? data.client.email.trim().toLowerCase() : undefined,
      clientContact: data.client.contact?.trim() || undefined,
      startTime: data.timeSlot.startTime,
      endTime: data.timeSlot.endTime,
      locationId: data.locationId,
      addressLine1: data.address?.addressLine1,
      addressLine2: data.address?.addressLine2,
      addressCity: data.address?.city,
      addressState: data.address?.state,
      addressZipCode: data.address?.zipCode,
      addressCountry: data.address?.country,
      addressLatitude: data.addressLatitude,
      addressLongitude: data.addressLongitude
    };

    const result = await BrowserApiClient.createProfessionalBooking(request);

    return {
      success: true,
      booking: result,
      message: `Professional booking created successfully! Booking ID: ${result.id}`
    };
  } catch (error) {
    console.error('Failed to create professional booking:', error);

    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to create professional booking')
    };
  }
}

export async function deleteProfessionalBooking(
  bookingId: string,
  reason?: string
): Promise<DeleteProfessionalBookingResult> {
  try {
    const params: DeleteProfessionalBookingParams = {
      ...(reason && { reason })
    };

    const response = await BrowserApiClient.deleteProfessionalBooking(bookingId, params);

    return {
      success: true,
      data: response,
      message: 'Booking cancelled successfully'
    };
  } catch (error) {
    console.error('Error deleting professional booking:', error);

    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to cancel booking'),
      message: 'An error occurred while cancelling the booking'
    };
  }
}

/**
 * Confirm a pending booking (cash or free).
 * The backend determines the appropriate status based on booking data:
 * - If totalPrice === 0 → FREE
 * - If slotPaymentMethods includes 'cash' → CASH
 *
 * @param bookingId - The booking ID to confirm
 * @returns The confirmed booking data
 *
 * Note: Uses browserFetch directly until API client is regenerated.
 */
export async function confirmBooking(bookingId: string): Promise<BookingResponse> {
  return browserFetch<BookingResponse>(`/bookings/${bookingId}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ bookingId })
  });
}
